import {
  FlightsFareTypes,
  FlightSortOptions,
  getAdultPassengerState,
  getChildPassengerState,
  getInfantPassengerState,
  getPrimaryPassengerState,
  maxFlightInfantAge,
  maxFlightPassengerCount,
} from 'constants/flight'
import createSelector from 'lib/createSelector'
import moment from 'moment'
import qs from 'qs'
import { groupBy, last, min, nonNullable, sortByAll, SortField, sum, unique } from './arrayUtils'
import switchFunc from './switchFunc'
import { FlightDealDuration, FlightDropdownOption, PriceChanges } from 'components/Flights/types'
import { contains } from 'lib/stringUtils'
import { convertTimeObjectToMinutes } from './flightUtils'
import { DMY_CASUAL_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { without } from 'lib/arrayUtils'
import { SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'

// fareClassEconomy is an economy fare class
export const fareClassEconomy = 'Economy'
// fareClassBusiness is a business fare class
export const fareClassBusiness = 'Business'

// compareFareClasses is a comparison function to compare two fare classes using a total order
// implemented by the fare class.
function compareFareClasses(a, b) {
  if (a === b) {
    return 0
  }

  if (a === fareClassEconomy && b !== fareClassEconomy) {
    return -1
  }

  return 1
}

// getDisplayInfo returns an object containing information that summarizes the given array of flights
export function getDisplayInfo(flights) {
  let bestFareClass = fareClassEconomy
  const fareBookingClasses = {}
  const flightsWithFreeCheckInBaggage = {}
  let allFlightsHaveTheSameBaggage = true
  const baggageDetails = {
    freeCarryOnBaggageDescription: flights[0].freeCarryOnBaggageDescription,
    freeCheckedInBaggageDescription: flights[0].freeCheckedInBaggageDescription,
    freeCheckedInBaggageIncluded: flights[0].freeCheckedInBaggageIncluded,
  }

  for (let i = 0; i < flights.length; i++) {
    const flight = flights[i]

    fareBookingClasses[flight.bookingClass] = true

    if (flight.freeCheckedInBaggageIncluded) {
      flightsWithFreeCheckInBaggage[i] = true
    }

    if (allFlightsHaveTheSameBaggage && i > 0) {
      if (baggageDetails.freeCarryOnBaggageDescription !== flight.freeCarryOnBaggageDescription ||
        baggageDetails.freeCheckedInBaggageDescription !== flight.freeCheckedInBaggageDescription ||
        baggageDetails.freeCheckedInBaggageIncluded !== flight.freeCheckedInBaggageIncluded
      ) {
        allFlightsHaveTheSameBaggage = false
      }
    }

    if (compareFareClasses(flight.fareClass, bestFareClass) > 0) {
      bestFareClass = flight.fareClass
    }
  }

  return {
    hasMultipleFareBookingClasses: Object.keys(fareBookingClasses).length > 1,
    bestFareClass,
    baggageIncluded: Object.keys(flightsWithFreeCheckInBaggage).length === flights.length,
    allFlightsHaveTheSameBaggage,
  }
}

export function getFreeCheckedBaggage(flight: App.FlightV2): App.BaggageOption | undefined {
  return flight.checkedBaggageOptions.find(option => option.includedInFare)
}

export function getFreeCarryOnBaggage(flight: App.FlightV2): App.BaggageOption | undefined {
  return flight.carryOnBaggageOptions.find(option => option.includedInFare)
}

export function getDisplayInfoV2(flights: App.FlightV2[]) {
  let bestFareClass = fareClassEconomy
  const fareBookingClasses = {}
  const flightsWithFreeCheckInBaggage = {}
  let allFlightsHaveTheSameBaggage = true

  const baggageDetails = {
    freeCarryOnBaggageDescription: getFreeCarryOnBaggage(flights[0])?.description,
    freeCheckedInBaggageDescription: getFreeCheckedBaggage(flights[0])?.description,
    freeCheckedInBaggageIncluded: !!getFreeCheckedBaggage(flights[0]),
  }

  for (let i = 0; i < flights.length; i++) {
    const flight = flights[i]

    fareBookingClasses[flight.bookingClass] = true

    if (getFreeCheckedBaggage(flight)) {
      flightsWithFreeCheckInBaggage[i] = true
    }

    if (allFlightsHaveTheSameBaggage && i > 0) {
      if (baggageDetails.freeCarryOnBaggageDescription !== getFreeCarryOnBaggage(flight)?.description ||
        baggageDetails.freeCheckedInBaggageDescription !== getFreeCheckedBaggage(flight)?.description ||
        baggageDetails.freeCheckedInBaggageIncluded !== !!getFreeCheckedBaggage(flight)
      ) {
        allFlightsHaveTheSameBaggage = false
      }
    }

    if (compareFareClasses(flight.fareClass, bestFareClass) > 0) {
      bestFareClass = flight.fareClass
    }
  }

  return {
    hasMultipleFareBookingClasses: Object.keys(fareBookingClasses).length > 1,
    bestFareClass,
    baggageIncluded: Object.keys(flightsWithFreeCheckInBaggage).length === flights.length,
    allFlightsHaveTheSameBaggage,
  }
}

const getFilteredReturningJourneys = createSelector(
  (state: App.State) => state.flights.journeys,
  (state: App.State) => state.flights.flightSearchV2?.onewayFares,
  (state: App.State) => state.flights.filters,
  (journeys: Array<App.Journey>, oneWayFares: App.OnewayFare[], filters: Array<any>) => oneWayFares?.length > 0 ? oneWayFares[1].fares : journeys.map<App.Journey>(journey => ({
    ...journey,
    hidden: !filters.every(f => {
      const activeOptions = f.options.filter(option => option.active)
      const isAllOptionActive = activeOptions.find(ao => ao.value === 'all')

      if (isAllOptionActive) {
        return true
      }
      return activeOptions.some(af => f.fn(journey, af.value))
    }),
  })),
)

const getFilteredDepartingJourneys = createSelector(
  (state: App.State) => state.flights.journeys,
  (state: App.State) => state.flights.flightSearchV2?.onewayFares,
  (state: App.State) => state.flights.filters,
  (journeys: Array<App.Journey>, oneWayFares: App.OnewayFare[], filters: Array<any>) => oneWayFares?.length > 0 ? oneWayFares[0].fares : journeys.map<App.Journey>(journey => ({
    ...journey,
    hidden: !filters.every(f => {
      const activeOptions = f.options.filter(option => option.active)
      const isAllOptionActive = activeOptions.find(ao => ao.value === 'all')

      if (isAllOptionActive) {
        return true
      }
      return activeOptions.some(af => f.fn(journey, af.value))
    }),
  })),
)

const flightPriceSort = { selector: j => j.cost, direction: 'asc' } as SortField<App.Journey>

const departingFlightStopoverSort = { selector: j => j.departing.flights.length, direction: 'asc' } as SortField<App.Journey>
const departingFlightTimeSort = { selector: j => j.departing.flightTime ? convertTimeObjectToMinutes(j.departing.flightTime) : undefined, direction: 'asc' } as SortField<App.Journey>
const departingDepartureSort = { selector: j => +new Date(`${j.departing.flights[0].departingDate}T${j.departing.flights[0].departingTime}`), direction: 'asc' } as SortField<App.Journey>
const departingDepartureDescSort = { selector: j => +new Date(`${j.departing.flights[0].departingDate}T${j.departing.flights[0].departingTime}`), direction: 'desc' } as SortField<App.Journey>
const departingArrivalSort = { selector: j => +new Date(`${last(j.departing.flights).arrivalDate}T${last(j.departing.flights).arrivalTime}`), direction: 'asc' } as SortField<App.Journey>

const returningFlightStopoverSort = { selector: j => j.returning!.flights.length, direction: 'asc' } as SortField<App.Journey>
const returningFlightTimeSort = { selector: j => j.returning?.flightTime ? convertTimeObjectToMinutes(j.returning.flightTime) : undefined, direction: 'asc' } as SortField<App.Journey>
const returningDepartureSort = { selector: j => j.returning ? +new Date(`${j.returning.flights[0].departingDate}T${j.returning.flights[0].departingTime}`) : undefined, direction: 'asc' } as SortField<App.Journey>
const returningDepartureDescSort = { selector: j => j.returning ? +new Date(`${j.returning.flights[0].departingDate}T${j.returning.flights[0].departingTime}`) : undefined, direction: 'desc' } as SortField<App.Journey>
const returningArrivalSort = { selector: j => j.returning ? +new Date(`${last(j.returning.flights).arrivalDate}T${last(j.returning.flights).arrivalTime}`) : undefined, direction: 'asc' } as SortField<App.Journey>

export const getDepartingSortList = switchFunc<Array<SortField<App.Journey>>>({
  [FlightSortOptions.Best]: [departingFlightStopoverSort, flightPriceSort, departingFlightTimeSort, departingDepartureSort],
  [FlightSortOptions.Cheapest]: [flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort, departingDepartureSort],
  [FlightSortOptions.Fastest]: [departingFlightTimeSort, flightPriceSort, departingDepartureSort, departingFlightTimeSort],
  [FlightSortOptions.EarliestTakeOff]: [departingDepartureSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.LatestTakeOff]: [departingDepartureDescSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.EarliestLanding]: [departingArrivalSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
}, [flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort, departingDepartureSort, departingDepartureDescSort, departingArrivalSort])

const flightPriceSortV2 = { selector: j => j.price.all.totalFare, direction: 'asc' } as SortField<App.JourneyV2>
const departingFlightStopoverSortV2 = { selector: j => j.flightGroups[0].flights.length, direction: 'asc' } as SortField<App.JourneyV2>
const departingFlightTimeSortV2 = { selector: j => convertTimeObjectToMinutes(j.flightGroups[0].totalFlightDuration), direction: 'asc' } as SortField<App.JourneyV2>
const departingDepartureSortV2 = { selector: j => +new Date(`${j.flightGroups[0].flights[0].departingDate}T${j.flightGroups[0].flights[0].departingTime}`), direction: 'asc' } as SortField<App.JourneyV2>
const departingDepartureDescSortV2 = { selector: j => +new Date(`${j.flightGroups[0].flights[0].departingDate}T${j.flightGroups[0].flights[0].departingTime}`), direction: 'desc' } as SortField<App.JourneyV2>
const departingArrivalSortV2 = { selector: j => +new Date(`${last(j.flightGroups[0].flights).arrivalDate}T${last(j.flightGroups[0].flights).arrivalTime}`), direction: 'asc' } as SortField<App.JourneyV2>

const returningFlightStopoverSortV2 = { selector: j => j.flightGroups[0].flights.length, direction: 'asc' } as SortField<App.JourneyV2>
const returningFlightTimeSortV2 = { selector: j => convertTimeObjectToMinutes(j.flightGroups[0].totalFlightDuration), direction: 'asc' } as SortField<App.JourneyV2>
const returningDepartureSortV2 = { selector: j => +new Date(`${j.flightGroups[0].flights[0].departingDate}T${j.flightGroups[0].flights[0].departingTime}`), direction: 'asc' } as SortField<App.JourneyV2>
const returningDepartureDescSortV2 = { selector: j => +new Date(`${j.flightGroups[0].flights[0].departingDate}T${j.flightGroups[0].flights[0].departingTime}`), direction: 'desc' } as SortField<App.JourneyV2>
const returningArrivalSortV2 = { selector: j => +new Date(`${last(j.flightGroups[0].flights).arrivalDate}T${last(j.flightGroups[0].flights).arrivalTime}`), direction: 'asc' } as SortField<App.JourneyV2>

export const getDepartingSortListV2 = switchFunc<Array<SortField<App.JourneyV2>>>({
  [FlightSortOptions.Best]: [departingFlightStopoverSortV2, flightPriceSortV2, departingFlightTimeSortV2, departingDepartureSortV2],
  [FlightSortOptions.Cheapest]: [flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2, departingDepartureSortV2],
  [FlightSortOptions.Fastest]: [departingFlightTimeSortV2, flightPriceSortV2, departingDepartureSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.EarliestTakeOff]: [departingDepartureSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.LatestTakeOff]: [departingDepartureDescSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.EarliestLanding]: [departingArrivalSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
}, [flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2, departingDepartureSortV2, departingDepartureDescSortV2, departingArrivalSortV2])

export const getReturningSortListV2 = switchFunc<Array<SortField<App.JourneyV2>>>({
  [FlightSortOptions.Best]: [returningFlightStopoverSortV2, flightPriceSortV2, returningFlightTimeSortV2, returningDepartureSortV2],
  [FlightSortOptions.Cheapest]: [flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2, returningDepartureSortV2],
  [FlightSortOptions.Fastest]: [returningFlightTimeSortV2, flightPriceSortV2, returningDepartureSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.EarliestTakeOff]: [returningDepartureSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.LatestTakeOff]: [returningDepartureDescSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.EarliestLanding]: [returningArrivalSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
}, [flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2, returningDepartureSortV2, returningDepartureDescSortV2, returningArrivalSortV2])

export function getUniqueDepartingFlights(journeys: Array<App.Journey>) {
  const grouped = groupBy(journeys, j => j.departing.journeyKey)
  return Array.from(grouped.values()).map(group => {
    const visibleJourneys = group.filter(j => !j.hidden)
    return min(visibleJourneys.length === 0 ? group : visibleJourneys, val => val.cost)
  })
}

// getDepartingJourneys returns an array of journeys with unique departing journey key.
// Concretely, it de-duplicates journeys by its 'departing' object's 'journeyKey'. Any tie is
// broken by choosing the object from the journey with a lower cost.
export const getDepartingJourneys = createSelector(
  (state: App.State) => getFilteredDepartingJourneys(state),
  (state: App.State) => state.flights.sort,
  (journeys: Array<App.AnyJourney>, sortValue) => {
    if (isJourneyV2Items(journeys)) {
      return sortByAll(nonNullable(journeys), getDepartingSortListV2(sortValue))
    }
    const uniqueJourneys = getUniqueDepartingFlights(journeys as App.Journey[])

    return sortByAll(nonNullable(uniqueJourneys), getDepartingSortList(sortValue))
  },
)

export const getReturningSortList = switchFunc<Array<SortField<App.Journey>>>({
  [FlightSortOptions.Best]: [returningFlightStopoverSort, flightPriceSort, returningFlightTimeSort, returningDepartureSort],
  [FlightSortOptions.Cheapest]: [flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort, returningDepartureSort],
  [FlightSortOptions.Fastest]: [returningFlightTimeSort, flightPriceSort, returningDepartureSort, returningFlightTimeSort],
  [FlightSortOptions.EarliestTakeOff]: [returningDepartureSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.LatestTakeOff]: [returningDepartureDescSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.EarliestLanding]: [returningArrivalSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
}, [flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort, returningDepartureSort, returningDepartureDescSort, returningArrivalSort])

export const getReturningJourneys = createSelector(
  (state: App.State) => getFilteredReturningJourneys(state),
  (state: App.State) => state.cart.flight.journey.departing.journeyKey,
  (state: App.State) => state.flights.sort,
  (journeys, departingKey, sortValue) => {
    if (isJourneyV2Items(journeys)) {
      return sortByAll(journeys, getReturningSortListV2(sortValue))
    }
    const departureMatchedJourneys = journeys.filter(journey => journey.departing.journeyKey === departingKey)

    return sortByAll(departureMatchedJourneys, getReturningSortList(sortValue))
  },
)

export function cartIncludesFlights(cart: App.CartState) {
  const { journey } = cart.flight

  if (journey.fareType && isOneWayFlight(journey.fareType)) {
    return journey.departing.flights.length > 0
  } else {
    return journey.departing.flights.length > 0 &&
      (journey.returning?.flights.length || 0) > 0
  }
}

export function tripType(journey: App.Journey) {
  return journey.departing.flights[0].departingDisplayNames.departingCountry === journey.returning?.flights[0].departingDisplayNames.departingCountry ?
    'domestic' : 'international'
}

const createPrimary = (): App.Passenger => {
  const passenger = getPrimaryPassengerState()
  return {
    id: 'adult-0',
    type: 'adult',
    ...passenger,
  }
}

const createAdult = (index: number): App.Passenger => {
  const passenger = getAdultPassengerState()
  return {
    id: `adult-${index}`,
    type: 'adult',
    ...passenger,
  }
}

const createChild = (index): App.Passenger => {
  const passenger = getChildPassengerState()
  return {
    id: `child-${index}`,
    type: 'child',
    ...passenger,
  }
}

const createInfant = (index): App.Passenger => {
  const passenger = getInfantPassengerState()
  return {
    id: `infant-${index}`,
    type: 'infant',
    ...passenger,
  }
}

function createPassengerArray(passengerCreator: (num: number) => App.Passenger, length: number, baseIndex: number): App.Passenger[] {
  const arr: App.Passenger[] = []
  for (let i = 0; i < length; i++) {
    arr.push(passengerCreator(baseIndex + i))
  }

  return arr
}

/**
 * Build list of pax for the new checkout cart item state
 *
 * @remarks
 * Main difference from "createPassengers()" is that the `id` suffix starts at 1 instead of 0
 *   i.e. 'adult-1'
 */
export function newCheckoutCreatePassengers({ adults, children = 0, infants = 0 }: App.Occupants) {
  const adultPaxList: App.Passenger[] = []
  const childPaxList: App.Passenger[] = []
  const infantList: App.Passenger[] = []

  for (let i = 0; i < adults; i++) { adultPaxList.push(createAdult(i + 1)) }
  for (let i = 0; i < children; i++) { childPaxList.push(createChild(i + 1)) }
  for (let i = 0; i < infants; i++) { infantList.push(createInfant(i + 1)) }

  return [...adultPaxList, ...childPaxList, ...infantList]
}

export function createPassengers({ adults, children, infants }: App.Occupants) {
  const primary = createPrimary()
  const mappedAdults = createPassengerArray(createAdult, adults - 1, 1)
  const mappedChildren = createPassengerArray(createChild, (children ?? 0), adults)
  const mappedInfants = createPassengerArray(createInfant, (infants ?? 0), adults + (children ?? 0))

  return [
    primary,
    ...mappedAdults,
    ...mappedChildren,
    ...mappedInfants,
  ]
}

function restorePassengerWithBaggage(storedPassenger: App.Passenger, index) {
  if (storedPassenger.isPrimary) {
    return createPrimary()
  } else if (storedPassenger.type === 'adult') {
    return createAdult(index)
  } else if (storedPassenger.type === 'child') {
    return createChild(index)
  } else {
    return createInfant(index)
  }
}

export function restorePassengersWithBaggage(storedPassengers: Array<App.Passenger>) {
  if (!storedPassengers.some(p => p.isPrimary)) {
    return undefined // Something's screwed up, we're missing a primary passenger
  }

  return storedPassengers.map((passenger, index) => ({
    ...restorePassengerWithBaggage(passenger, index),
    ...passenger,
  }))
}

export function parseContactNumber(number: App.PassengerPhoneNumber) {
  // attempt to remove any code from the phone input just in case the user entered
  // the code into the phone input as well as selecting a code
  return `${number.prefix}${number.phone.replace(new RegExp(`^([+]?${number.prefix}|0+)`), '')}`
}

function getPassengerAddedBaggage(passengers: Array<App.Passenger>, journeyId: string): App.AddedBaggage[][] {
  return nonNullable(passengers.map(p => {
    if (p?.addedExtras?.baggage && p.addedExtras.baggage[journeyId]) {
      return p.addedExtras.baggage[journeyId]
    }
    return []
  }))
}

export function getBaggageCount(passengers: Array<App.Passenger>, journeySegment: App.JourneyFlight) {
  const journeyId = journeySegment.journeyKey
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyId)
  return sum(passengerAddedBaggage, passengerBaggage => sum(passengerBaggage, o => o.count))
}

export function getBaggageCost(passengers: Array<App.Passenger>, journeySegment: App.JourneyFlight) {
  const journeyId = journeySegment.journeyKey
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyId)
  return sum(passengerAddedBaggage, (passengerBaggage) => (
    sum(passengerBaggage, selectedOption => {
      const option = journeySegment.extras?.baggage?.find(availableOption => availableOption.id === selectedOption.id)
      return option ? selectedOption.count * option.amount : 0
    })
  ))
}

export function resetFilter(filter: App.FlightFilter) {
  return {
    ...filter,
    options: filter.options.map(o => ({
      ...o,
      active: !!o.isDefault,
    })),
  }
}

export function updateFilter(filter: App.FlightFilter, newOption: App.FlightFilterOption, selectedOptionOnly: boolean) {
  return {
    ...filter,
    options: filter.options.map(o => {
      // If "select only" option select => only that should be select
      // If "all" select => de-select all other options
      // If any other option select => de-select "all" option
      // all option & other option can't select at one time

      if (selectedOptionOnly) {
        return {
          ...o,
          active: o === newOption,
        }
      }

      if (newOption.value === 'all') {
        return {
          ...o,
          active: o === newOption ? !o.active : false,
        }
      }

      if (o.value === 'all') {
        return {
          ...o,
          active: false,
        }
      } else if (o === newOption) {
        return {
          ...o,
          active: !o.active,
        }
      }

      return o
    }),
  }
}

export function getSortingLabel(selectedOption: string) {
  switch (selectedOption) {
    case FlightSortOptions.EarliestTakeOff:
      return {
        sortingTitle: 'Take-off:',
        sortingLabel: 'Earliest',
      }
    case FlightSortOptions.LatestTakeOff:
      return {
        sortingTitle: 'Take-off:',
        sortingLabel: 'Latest',
      }
    case FlightSortOptions.EarliestLanding:
      return {
        sortingTitle: 'Landing:',
        sortingLabel: 'Earliest',
      }
    default:
      return {
        sortingLabel: 'Other',
      }
  }
}

export function getOtherSortOptions(originCode: string, destinationCode: string, segment: string): Array<FlightDropdownOption<FlightSortOptions>> {
  const takeOffAirport = segment === 'departing' ? originCode : destinationCode
  const landingAirport = segment === 'departing' ? destinationCode : originCode

  return [{
    value: FlightSortOptions.EarliestTakeOff,
    label: `Earliest take-off (${takeOffAirport})`,
  }, {
    value: FlightSortOptions.LatestTakeOff,
    label: `Latest take-off (${takeOffAirport})`,
  }, {
    value: FlightSortOptions.EarliestLanding,
    label: `Earliest landing (${landingAirport})`,
  }]
}

export function filterAirlinesByCarrier(filters: Array<App.FlightFilter>, carrier: String): App.FlightFilter | undefined {
  const airlineFilter = filters.find(filter => filter.id === 'airline')

  if (airlineFilter) {
    return {
      ...airlineFilter,
      options: airlineFilter.options.map(option => ({
        ...option,
        active: option.value === carrier,
      })),
    }
  }

  return undefined
}

export function calcPriceChanges(departingPriceSnapshot: number, journeyCost: number) {
  const priceChanges: PriceChanges = {
    wentUp: false,
    stayedTheSame: true,
    amount: 0,
  }

  if (journeyCost > departingPriceSnapshot) {
    priceChanges.wentUp = true
    priceChanges.stayedTheSame = false
    priceChanges.amount = (journeyCost - departingPriceSnapshot)
  }

  if (journeyCost < departingPriceSnapshot) {
    priceChanges.wentDown = true
    priceChanges.stayedTheSame = false
    priceChanges.amount = (departingPriceSnapshot - journeyCost)
  }

  if (journeyCost === departingPriceSnapshot) {
    priceChanges.wentUp = false
    priceChanges.wentDown = false
    priceChanges.stayedTheSame = true
    priceChanges.amount = 0
  }

  return priceChanges
}

export function calcPriceDiff(departingPriceSnapshot: number, journeyCost: number): PriceChanges {
  return {
    amount: journeyCost - departingPriceSnapshot,
  }
}

export function getFareClass(flightClass: string) {
  if (contains(flightClass, 'biz') || contains(flightClass, 'business')) {
    return 'Business'
  } else if (contains(flightClass, 'first')) {
    return 'First'
  } else if (contains(flightClass, 'premium')) {
    return 'Premium'
  }
  return 'Economy'
}

export function getAirportDisplayText(airport: App.AirportLocation) {
  const {
    cityCode,
    isMultiAirport,
    isChildDisplay,
    airportName,
    airportCode,
    cityAirportName,
  } = airport

  const isCityAirport = isMultiAirport && isChildDisplay
  const displayName = isCityAirport ? cityAirportName : airportName
  const displayCode = isCityAirport ? cityCode : airportCode

  return `${displayName} (${displayCode})`
}

export function validateOccupants(occupants: App.Occupants) {
  const totalPassengers = occupants.adults + (occupants?.children ?? 0)
  const totalInfants = occupants?.childrenAge?.filter((age) => age !== -1 && age <= maxFlightInfantAge).length ?? 0
  return totalPassengers > maxFlightPassengerCount || totalInfants > occupants.adults
}

export function getAirport(airport: App.Airport) {
  return {
    airportCode: airport.code,
    airportName: airport.name,
  }
}

export function isOneWayFlight(fareType: string): boolean {
  return !!fareType && fareType === FlightsFareTypes.ONE_WAY
}

function buildPaxCountMap(paxList: App.Checkout.FlightPassenger[]) {
  const numAdults = paxList.filter((pax) => pax.type === 'adult').length
  const numChildren = paxList.filter((pax) => pax.type === 'child').length
  const numInfants = paxList.filter((pax) => pax.type === 'infant').length
  return { numAdults, numChildren, numInfants }
}

export interface StandaloneFlightSearchParam {
  originAirportCode: string;
  originAirportName: string;
  destinationAirportCode: string;
  destinationAirportName: string;
  departDate: string;
  returnDate: string | null;
  adults: number;
  children: number;
  infants: number;
  /** Comma-separated list of ages */
  childrenAge?: string;
  fareClass: string;
  fareType: 'return' | 'oneWay' | 'multiCity';
}

export function buildStandaloneFlightSearchUrl(params: StandaloneFlightSearchParam) {
  return `/flights-search-results?${qs.stringify(params)}`
}

/**
 * @deprecated Replaced by "getNewStandaloneFlightSearchURL"
 */
export function getStandaloneFlightSearchURL(flight: App.CartFlight) {
  const { journey, passengers, flightOrigin, flightDestination, flightOriginName, flightDestinationName } = flight
  const paxCountMap = buildPaxCountMap(passengers)
  const isOneWayFare = isOneWayFlight(journey.fareType ?? 'return')

  return buildStandaloneFlightSearchUrl({
    originAirportCode: flightOrigin,
    originAirportName: flightOriginName,
    destinationAirportCode: flightDestination,
    destinationAirportName: flightDestinationName,
    departDate: moment(journey.departing.flights[0].departingDate).format(ISO_DATE_FORMAT),
    returnDate: !isOneWayFare && journey?.returning ? moment(journey.returning.flights[0].departingDate).format(ISO_DATE_FORMAT) : null,
    adults: paxCountMap.numAdults,
    children: paxCountMap.numChildren,
    infants: paxCountMap.numInfants,
    fareClass: journey.fareClass?.toLowerCase() || 'economy',
    fareType: journey.fareType ?? 'return',
  })
}

export function getBundledFlightSearchURL(flight: App.CartFlight) {
  const { departing, returning } = flight.journey
  const [departingFlight] = departing.flights
  const [returningFlight] = returning ? returning.flights : [undefined]

  const flightSearchParams = {
    departDate: departingFlight.departingDate,
    returnDate: returningFlight?.departingDate,
    originAirportCode: departingFlight.departingAirport,
    destinationAirportCode: returningFlight?.departingAirport,
  }

  return `/booking/departing?${qs.stringify(flightSearchParams)}`
}

export function getFlightDealDuration(offerDuration: Array<FlightDealDuration>) {
  return offerDuration.map((duration, index) => {
    const { departureDate, arrivalDate } = duration

    if (departureDate && arrivalDate) {
      const dateLabel = `${moment(departureDate).format(DMY_CASUAL_FORMAT)} - ${moment(arrivalDate).format(DMY_CASUAL_FORMAT)}`
      return `${index !== 0 ? '   •   ' : ''}${dateLabel}`
    }
  })
}

export function getOperatingCarrierText(flights: Array<App.Flight>) {
  const differentOperatingFlights = flights.filter(flight => flight.carrier !== flight.operatingCarrier)

  if (differentOperatingFlights.length !== 0) {
    return differentOperatingFlights[0].operatedByText
  }
  return null
}

export function filterAirportRecords(records: Array<App.AirportLocation>, excludingAirport?: App.AirportLocation) {
  if (records) {
    const excludingRecord = records.find(record => record.airportCode === excludingAirport?.airportCode)
    const airportsWithoutSelections = without(records, excludingRecord)
    const groupedAirportRecords = groupBy(airportsWithoutSelections, record => record?.cityCode)

    return nonNullable(Array.from(groupedAirportRecords.entries()).map(groupedAirports =>
      groupedAirports[1].map((airport, index) => {
        if (!airport) return
        return {
          ...airport,
          isCityAirport: index !== 0,
        }
      }),
    ).flat())
  }
  return []
}

export function isJourneyV2Items(items: App.AnyJourney[]): items is App.JourneyV2[] {
  return items?.[0]?.itemType === JOURNEY_TYPE_V2
}

export function isJourneyV2(item: App.AnyJourney): item is App.JourneyV2 {
  return item?.itemType === JOURNEY_TYPE_V2
}

export function getBaggageCostV2(passengers: Array<App.Passenger>, journey: App.JourneyV2) {
  const journeyId = journey.bookingInfo.journeyKey
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyId)
  const flight = journey.flightGroups[0].flights[0]

  return sum(passengerAddedBaggage, (passengerBaggage) => (
    sum(passengerBaggage, selectedOption => {
      const option = flight.checkedBaggageOptions.find(availableOption => availableOption.id === selectedOption.id)
      return option ? selectedOption.count * option.amount : 0
    })
  ))
}

export function getBaggageCountV2(passengers: Array<App.Passenger>, journeyV2: App.JourneyV2) {
  const passengerAddedBaggage = getPassengerAddedBaggage(passengers, journeyV2.bookingInfo.journeyKey)
  return sum(passengerAddedBaggage, passengerBaggage => sum(passengerBaggage, o => o.count))
}

export const JOURNEY_TYPE_V1 = 'journeyV1'
export const JOURNEY_TYPE_V2 = 'journeyV2'

export function getAirlineLogoAndCarriers(carrierNames: string, carrierLogos: string) {
  const uniqueCarrierLogos = unique(carrierLogos.split('|'))
  const uniqueCarrierNames = unique(carrierNames.split('|'))

  return [uniqueCarrierLogos, uniqueCarrierNames]
}

export function getMainAirlineLogo(carrierLogos?: string) {
  return carrierLogos?.split('|')?.[0]
}

export function compareAirportCodes(originAirport: string, destinationAirport: string) {
  const regex = new RegExp('ALL$')
  return originAirport.replace(regex, '') === destinationAirport.replace(regex, '')
}

export function filterSameDayFares(outboundFare: App.JourneyV2, inboundFares: App.JourneyV2[]) {
  const lastOutboundFlight = last(outboundFare.flightGroups[0].flights)
  // 5 Hours is for ticketing time and change overtime after testing it would become 3
  const arrivalTime = moment(lastOutboundFlight.arrivalTime, SHORT_TIME_FORMAT_24_HOUR).add(5, 'hours')

  return inboundFares.filter((fare) => {
    const firstInboundFlight = fare.flightGroups[0].flights[0]
    const departingTime = moment(firstInboundFlight.departingTime, SHORT_TIME_FORMAT_24_HOUR)
    return departingTime.isAfter(arrivalTime)
  })
}

export function calcTotalDisplayPrice(isDeparting: boolean, journeyPrice: number, departingFarePrice?: number) {
  if (isDeparting) return null
  return journeyPrice + (departingFarePrice ?? 0)
}
